import produce from 'immer';

import * as fromActions from './bdcdashboard.actions';
import * as fromAdapter from './bdcdashboard.adapter';
import { IAppointment, IAsyncData, IVehicle } from '@signal/asp-data-commons';
import {upsert} from '../../shared/services/util.service';

export interface IPendingAppointmentState extends IAsyncData<IAppointment[]> {
  allPendingAppointmentsCount : number;
  filteredPendingAppointmentsCount: number;
}

export interface IPendingResponse {
  appointment: IAppointment[];
   allAppointmentsCount: number;
   filteredAppointmentCount: number;
}

export interface BdcDashboardState {
  userVehicles: IVehicle[];
  userVehiclesLoading: boolean;
  userVehiclesLoaded: boolean;
  appointments: IAppointment[];
  appointmentsforvin: IAppointment[];
  appointmentsLoading: boolean;
  appointmentsLoaded: boolean;
  error: string;
  pendingAppointments : IPendingAppointmentState
}

const initialState: BdcDashboardState = {
  userVehicles: [],
  userVehiclesLoading: false,
  userVehiclesLoaded: false,
  appointments: [],
  appointmentsforvin: [],
  appointmentsLoading: false,
  appointmentsLoaded: false,
  error: '',
  pendingAppointments: {
    data: [],
    loading: false,
    loaded: false,
    error: '',
    allPendingAppointmentsCount: null,
    filteredPendingAppointmentsCount: null
  },

};

function updateMileageOfVehicleId(payload) {
  return uv => uv.vehicleId === payload.vehicleId ? {
    ...uv,
    mileage: payload.mileage
  } : uv;
}

export function bdcDashboardReducer(
  state: BdcDashboardState = initialState,
  action: fromActions.BdcDashboardActions): BdcDashboardState {

  switch (action.type) {
    case fromActions.BdcDashboardActionTypes.LOAD_USER_VEHICLES: {
      return produce(state, draftState => {
        draftState.userVehicles = [];
        draftState.userVehiclesLoaded = false;
        draftState.userVehiclesLoading = true;
        draftState.error = '';
      });
    }

    case fromActions.BdcDashboardActionTypes.LOAD_USER_VEHICLES_SUCCESS: {
      return produce(state, draftState => {
        draftState.userVehicles = action instanceof fromActions.LoadUserVehiclesSuccess ? action.payload.vehicles : [];
        draftState.userVehiclesLoaded = true;
        draftState.userVehiclesLoading = false;
        draftState.error = '';
      });
    }

    case fromActions.BdcDashboardActionTypes.LOAD_USER_VEHICLES_FAILURE: {
      return produce(state, draftState => {
        draftState.userVehicles = [];
        draftState.userVehiclesLoaded = false;
        draftState.userVehiclesLoading = false;
        draftState.error = action instanceof fromActions.LoadUserVehiclesFail ? action.payload.error : '';
      });
    }

    case fromActions.BdcDashboardActionTypes.CLEAR_USER_VEHICLES: {
      return produce(state, draftState => {
        draftState.userVehicles = [];
        draftState.userVehiclesLoaded = true;
        draftState.userVehiclesLoading = false;
        draftState.error = '';
      });
    }

    case fromActions.BdcDashboardActionTypes.LOAD_APPOINTMENTS: {
      return produce(state, draftState => {
        draftState.appointments = [];
        draftState.appointmentsLoaded = false;
        draftState.appointmentsLoading = true;
        draftState.error = '';
      });
    }
    case fromActions.BdcDashboardActionTypes.LOAD_APPOINTMENTS_FOR_VIN: {
      return produce(state, draftState => {
        draftState.appointmentsforvin = [];
        draftState.appointmentsLoaded = false;
        draftState.appointmentsLoading = true;
        draftState.error = '';
      });
    }
    case fromActions.BdcDashboardActionTypes.LOAD_APPOINTMENTS_SUCCESS: {
      return produce(state, draftState => {
        draftState.appointments = action instanceof fromActions.LoadAppointmentsSuccess ? action.payload.appointments : [];
        draftState.appointmentsLoaded = true;
        draftState.appointmentsLoading = false;
        draftState.error = '';
      });
    }
    case fromActions.BdcDashboardActionTypes.LOAD_APPOINTMENTS_SUCCESS_FOR_VIN: {
      return produce(state, draftState => {
        draftState.appointmentsforvin = action instanceof fromActions.LoadAppointmentsSuccessForVin ? action.payload.appointments : [];
        draftState.appointmentsLoaded = true;
        draftState.appointmentsLoading = false;
        draftState.error = '';
      });
    }

    case fromActions.BdcDashboardActionTypes.LOAD_APPOINTMENTS_FAILURE: {
      return produce(state, draftState => {
        draftState.appointments = [];
        draftState.appointmentsLoaded = false;
        draftState.appointmentsLoading = false;
        draftState.error = action instanceof fromActions.LoadAppointmentsFail ? action.payload.error : '';
      });
    }

    case fromActions.BdcDashboardActionTypes.UPSERT_APPOINTMENT: {
      return produce(state, draftState => {
        const appointment = action instanceof fromActions.UpsertAppointment ? action.payload.appointment : undefined;
        draftState.appointments = upsert([...state.appointments], appointment, appt => appt.appointmentId === appointment.appointmentId);
      });
    }

    case fromActions.BdcDashboardActionTypes.CANCEL_APPOINTMENT_FROM_BDCDASHBOARD:
    case fromActions.BdcDashboardActionTypes.CANCEL_APPOINTMENT_FROM_CONFIRM: {
      return produce(state, draftState => {
        draftState.appointments = draftState.appointments
          .map(fromAdapter.updateAppointmentAsCancelling(action));
      });
    }

    case fromActions.BdcDashboardActionTypes.CANCEL_APPOINTMENT_SUCCESS: {
      return produce(state, draftState => {
        const payloadAppointmentId = action instanceof fromActions.CancelAppointmentSuccess ? action.payload.appointmentId : '';
        draftState.appointments = draftState.appointments
          .filter(a => a.appointmentId !== payloadAppointmentId);
      });
    }

    case fromActions.BdcDashboardActionTypes.CANCEL_APPOINTMENT_FAILURE: {
      return produce(state, draftState => {
        draftState.error = action instanceof fromActions.CancelAppointmentFail ? action.payload.error : '';
      });
    }

    /* pending appointment screen */
    case fromActions.BdcDashboardActionTypes.LOAD_PENDING_APPOINTMENTS: {
      return produce(state, draftState => {
        draftState.pendingAppointments = {
          data: undefined,
          loading: true,
          loaded: false,
          error: '',
          allPendingAppointmentsCount: null,
          filteredPendingAppointmentsCount: null
        }
      })
    }

    case fromActions.BdcDashboardActionTypes.LOAD_PENDING_APPOINTMENTS_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadPendingAppointmentsSuccess ? action.payload : undefined;
        draftState.pendingAppointments = {
          data: payload.appointmentData.appointment,
          loading: false,
          loaded: true,
          error: '',
          allPendingAppointmentsCount: payload.isLoad ? payload.appointmentData.allAppointmentsCount : state.pendingAppointments.allPendingAppointmentsCount,
          filteredPendingAppointmentsCount: payload.appointmentData.filteredAppointmentCount
        }
      })
    }

    case fromActions.BdcDashboardActionTypes.LOAD_PENDING_APPOINTMENTS_FAILURE: {
      return produce(state, draftState => {
        draftState.pendingAppointments = {
          data: undefined,
          loading: false,
          loaded: false,
          error: action instanceof fromActions.LoadPendingAppointmentsFailure ? action.payload.error : '',
          allPendingAppointmentsCount: null,
          filteredPendingAppointmentsCount: null
        }
      })
    }

    case fromActions.BdcDashboardActionTypes.CANCEL_PENDING_APPOINTMENTS: {
      return produce(state, draftState => {
      });
    }

    case fromActions.BdcDashboardActionTypes.CANCEL_PENDING_APPOINTMENTS_SUCCESS: {
      return produce(state, draftState => {
        const payloadAppointmentId = action instanceof fromActions.CancelPendingAppointmentsSuccess ? action.payload.appointmentId : '';
        draftState.pendingAppointments.data = draftState.pendingAppointments.data
          .filter(a => a.appointmentId !== payloadAppointmentId);
        draftState.pendingAppointments.allPendingAppointmentsCount = state.pendingAppointments.allPendingAppointmentsCount - 1;
        draftState.pendingAppointments.filteredPendingAppointmentsCount = state.pendingAppointments.filteredPendingAppointmentsCount - 1;
      });
    }

    case fromActions.BdcDashboardActionTypes.CANCELPENDING_APPOINTMENTS_FAILURE: {
      return produce(state, draftState => {
        draftState.pendingAppointments.error = action instanceof fromActions.CancelPendingAppointmentsFail ? action.payload.error : '';
      });
    }

    case fromActions.BdcDashboardActionTypes.SEND_LINK_TO_CUSTOMER: {
      return produce(state, draftState => {
      });
    }

    case fromActions.BdcDashboardActionTypes.SEND_LINK_TO_CUSTOMER_SUCCESS: {
      return produce(state, draftState => {
        const payloadAppointmentId = action instanceof fromActions.SendLinkToCustomerSuccess ? action.payload.appointmentId : '';
        const index = draftState.pendingAppointments.data.findIndex(x => x.appointmentId === payloadAppointmentId);

        draftState.pendingAppointments.data[index] = {
          ...state.pendingAppointments.data[index],
          linkSentToCustomerOn: new Date().toISOString()
        };
      });
    }

    case fromActions.BdcDashboardActionTypes.SEND_LINK_TO_CUSTOMER_FAILURE: {
      return produce(state, draftState => {
        draftState.pendingAppointments.error = action instanceof fromActions.SendLinkToCustomerFailure ? action.payload.error : '';
      });
    }

    default:
      return state;
  }
}

